/// <amd-module name="Feed/Legacy/Components/Settings/ViewPanel" />
import * as React from "react";

import { CommandButton, DefaultButton, PrimaryButton } from "OfficeFabric/Button";
import { ColumnActionsMode, IColumn } from "OfficeFabric/DetailsList";
import { Overlay } from "OfficeFabric/Overlay";
import { Panel, PanelType } from "OfficeFabric/Panel";
import { TextField } from "OfficeFabric/TextField";

import { announce } from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { FormatComponent } from "VSS/Platform/Components/Format";

import { Checkbox } from "VSSUI/Checkbox";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TooltipHost } from "VSSUI/Tooltip";
import { VssDetailsList } from "VSSUI/VssDetailsList";

import { FwLinks, FeedSettingsComponents } from "Feed/Common/Constants/Constants";
import { FeedPermission, FeedRole, FeedView, FeedViewType, FeedVisibility } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { FeedSettingsActionCreator } from "Feed/Legacy/Actions/FeedSettingsActionCreator";
import { IdentityPickerWrapper } from "Feed/Legacy/Components/Settings/IdentityPickerWrapper";
import { RoleHelper } from "Feed/Legacy/Helpers/RoleHelper";
import { ExternalLink } from "Feed/Legacy/Components/ExternalLink";

// import { IEntity } from "VSS/Identities/Picker/RestClient";
export interface IViewPanelProps {
    pageContext: IVssPageContext;
    /**
     * Contains validation error messages for view-name
     */
    validationErrorBag: { [key: string]: string };

    /**
     * Open/Close the panel
     */
    isOpen: boolean;

    /**
     * add or edit
     */
    isViewPanelInEditMode?: boolean;

    /**
     * view permissions
     */
    viewPermissions: FeedPermission[];

    /**
     * selectedView, for edit only
     */
    selectedView?: FeedView;

    /**
     * defaultViewId, for edit only
     */
    defaultViewId: string;

    /**
     * True if the current organization is AAD
     */
    isAadUpstreamsFeatureEnabled: boolean;

    /**
     * True if ProjectScopedUpstreams feature flag is enabled
     */
    isProjectScopedUpstreamsFeatureEnabled: boolean;

    /**
     * True if the current user can make changes to the feed settings
     */
    isUserAdmin: boolean;

    /**
     * Tracks if views are getting saved to server
     */
    isSavingChanges: boolean;

    /**
     * While saving, captures errors if they occur to show in Panel
     */
    error: Error;

    /**
     * Indicates whether the feed is project scoped
     */
    isProjectScopedFeed: boolean;

    /**
     * Indicates whether the feed is public
     */
    isPublicFeed: boolean;

    /**
     * Name of collection
     */
    collectionName: string;

    /**
     * Name of feed
     */
    feedName: string;
}

export interface IViewPanelState {
    /**
     * View name entered in text box
     */
    viewName: string;

    /**
     * Tracks whether user wants to make this default view
     */
    defaultViewChecked: boolean;

    /**
     * Tracks whether user wants to use Organization, Collection, or Feed level visibility
     */
    selectedVisibilityType: FeedVisibility;

    /**
     * viewId for newly created view
     */
    newViewId: string;

    /**
     * view permission
     */
    viewPermissions: FeedPermission[];
}

export class ViewPanel extends React.Component<IViewPanelProps, IViewPanelState> {
    private _headerText: string;
    private _submitButtonText: string;
    private _onClickAction: () => void;
    private _disableSetDefaultView: boolean;
    // tslint:disable-next-line:no-any
    private _identity: any;

    constructor(props: IViewPanelProps) {
        super(props);
        this.state = this._getStateAndUpdateProperties(props);
    }

    public componentWillReceiveProps(nextProps: IViewPanelProps): void {
        if (this.props.isOpen !== nextProps.isOpen) {
            this.setState(this._getStateAndUpdateProperties(nextProps));
        }
    }

    public render(): JSX.Element {
        return (
            <Panel
                className={"view-panel"}
                isOpen={this.props.isOpen}
                onDismiss={this._onDismiss}
                type={PanelType.medium}
                isFooterAtBottom={true}
                onRenderFooterContent={this._onRenderFooterContent}
                headerText={this._headerText}
                closeButtonAriaLabel={PackageResources.AriaLabel_ClosePanel}
                hasCloseButton={this.props.isSavingChanges === false}
                focusTrapZoneProps={{ firstFocusableSelector: "view-name input" }}
            >
                {this.props.error != null && (
                    <MessageBar severity={MessageBarSeverity.Error}>{this.props.error.message}</MessageBar>
                )}
                <TextField
                    label={PackageResources.FeedSettings_ViewPanel_TextField_Label}
                    className={"view-name"}
                    defaultValue={this.state.viewName}
                    onChanged={this._onViewNameChanged}
                    errorMessage={this.props.validationErrorBag[FeedSettingsComponents.viewName]}
                    disabled={
                        this.props.isViewPanelInEditMode &&
                        this.props.selectedView &&
                        this.props.selectedView.type === FeedViewType.Implicit
                    }
                />
                <div>
                    <RadioButtonGroup
                        text={PackageResources.FeedSettings_ViewPanel_Visibility}
                        selectedButtonId={this.state.selectedVisibilityType.toString()}
                        onSelect={(selectedButtonId: string) =>
                            this.setState({ selectedVisibilityType: this._getVisibility(selectedButtonId) })
                        }
                    >
                        <div className="visibility-type-subtext">
                            {Utils_String.format(PackageResources.FeedSettings_ViewPanel_FeedReaders, this.props.feedName)}
                        </div>
                        {this._getVisibilityRadioButtons()}
                    </RadioButtonGroup>
                </div>
                {this.state.selectedVisibilityType === FeedVisibility.Private && (
                    <div className="visibility-type-subtext">
                        <MessageCard severity={MessageCardSeverity.Warning}>
                            <FormatComponent format={PackageResources.FeedSettings_ViewPanel_Visibility_Private_Warning}>
                                <ExternalLink
                                    pageContext={this.props.pageContext}
                                    className={"external-link margin-bottom"}
                                    href={FwLinks.ViewPermissionDetails}
                                >
                                    {PackageResources.FeedSettings_ViewPanel_Visibility_Permission_Details}
                                </ExternalLink>
                            </FormatComponent>
                        </MessageCard>
                        <IdentityPickerWrapper
                            pageContext={this.props.pageContext}
                            onIdentitiesResolved={this._onIdentitiesResolved}
                            multiIdentitySearch={false}
                        />
                        <PrimaryButton
                            disabled={this._isAddPermissionDisabled()}
                            onClick={this._addViewPermission}
                            text={PackageResources.FeedSettings_AddButton_Label}
                        />
                        <div className="permissions-content">
                            <div className="section-spacer" />
                            {this._getPermissionsMessageOrList()}
                        </div>
                    </div>
                )}
                <Checkbox
                    className={"default-view-checkbox"}
                    label={PackageResources.FeedSettings_ViewPanel_DefaultView_Label}
                    checked={this.state.defaultViewChecked}
                    onChange={this._onDefaultViewChange}
                    disabled={this._disableSetDefaultView}
                />
                {this.props.isSavingChanges && (
                    <Overlay className="feed-overlay">
                        <div className="content">
                            <Spinner
                                size={SpinnerSize.small}
                                label={PackageResources.FeedSettings_Overlay_SavingChanges}
                            />
                        </div>
                    </Overlay>
                )}
            </Panel>
        );
    }

    private _getVisibilityRadioButtons(): JSX.Element[] {
        const options = [];

        if (this.props.isAadUpstreamsFeatureEnabled) {
            options.push(
                <RadioButton
                    key={FeedVisibility.AadTenant.toString()}
                    id={FeedVisibility.AadTenant.toString()}
                    text={PackageResources.FeedSettings_ViewPanel_Visibility_AadTenant}
                    disabled={
                        this.props.isPublicFeed ||
                        (this.props.isProjectScopedFeed && !this.props.isProjectScopedUpstreamsFeatureEnabled)
                    }
                />
            );
        }

        options.push(
            <RadioButton
                key={FeedVisibility.Collection.toString()}
                id={FeedVisibility.Collection.toString()}
                text={Utils_String.format(
                    PackageResources.FeedSettings_ViewPanel_Visibility_Collection,
                    this.props.collectionName
                )}
                disabled={
                    this.props.isPublicFeed ||
                    (this.props.isProjectScopedFeed && !this.props.isProjectScopedUpstreamsFeatureEnabled)
                }
            />
        );

        options.push(
            <RadioButton
                key={FeedVisibility.Private.toString()}
                id={FeedVisibility.Private.toString()}
                text={PackageResources.FeedSettings_ViewPanel_Visibility_Private}
            />
        );

        return options;
    }

    private _getPermissionsMessageOrList(): JSX.Element {
        if (!this.state.viewPermissions || this.state.viewPermissions.length === 0) {
            return null;
        }

        return (
            <VssDetailsList
                className="permissions-grid"
                usePresentationStyles={true}
                presentationStyles={undefined}
                items={this.state.viewPermissions}
                selectionPreservedOnEmptyClick={true}
                allocateSpaceForActionsButtonWhileHidden={false}
                columns={this._getPermissionColumns()}
            />
        );
    }

    private _getPermissionColumns(): IColumn[] {
        return [
            {
                key: "entity",
                fieldName: "entity",
                name: PackageResources.FeedSettings_Permissions_Grid_UserGroup,
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (permission: FeedPermission): string => {
                    return permission.displayName;
                },
                minWidth: 200,
                maxWidth: 400,
                isResizable: true
            },
            {
                key: "role",
                fieldName: "role",
                name: PackageResources.FeedSettings_Permissions_Grid_Role,
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (permission: FeedPermission): string => {
                    return RoleHelper.roleToLocaleString(permission.role);
                },
                minWidth: 200,
                maxWidth: 200
            },
            {
                key: "delete",
                fieldName: "delete",
                name: PackageResources.FeedSettings_Grid_DeleteColumn,
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (permission: FeedPermission): JSX.Element => {
                    const iconClassName = this.props.isUserAdmin ? "edit-delete" : "edit-delete-disabled";
                    return (
                        <TooltipHost
                            content={PackageResources.FeedSettings_Permissions_Grid_RemovePermission_AriaLabel}
                        >
                            <CommandButton
                                className={"edit-delete-container"}
                                ariaLabel={PackageResources.FeedSettings_Permissions_Grid_RemovePermission_AriaLabel}
                                disabled={!this.props.isUserAdmin}
                                iconProps={{
                                    iconName: "Clear",
                                    className: iconClassName
                                }}
                                onClick={() => this._removeViewPermission(permission)}
                            />
                        </TooltipHost>
                    );
                },
                minWidth: 30,
                maxWidth: 30
            }
        ];
    }

    private _onRenderFooterContent = (): JSX.Element => {
        return (
            <div className="add-view-panel-footer">
                <PrimaryButton onClick={this._onClickAction} disabled={this._isAddDisabled()}>
                    {this._submitButtonText}
                </PrimaryButton>
                <DefaultButton onClick={this._onDismiss} disabled={this.props.isSavingChanges === true}>
                    {PackageResources.FeedSettings_CloseButton_Label}
                </DefaultButton>
            </div>
        );
    };

    private _getStateAndUpdateProperties(props: IViewPanelProps): IViewPanelState {
        let newState: IViewPanelState;
        let defaultVisibilityType: FeedVisibility;
        if (props.isPublicFeed || (props.isProjectScopedFeed && !props.isProjectScopedUpstreamsFeatureEnabled)) {
            defaultVisibilityType = FeedVisibility.Private;
        } else if (props.isAadUpstreamsFeatureEnabled) {
            defaultVisibilityType = FeedVisibility.AadTenant;
        } else {
            defaultVisibilityType = FeedVisibility.Collection;
        }

        if (props.isViewPanelInEditMode) {
            // edit
            newState = {
                viewName: props.selectedView.name,
                defaultViewChecked:
                    props.defaultViewId && props.selectedView && props.selectedView.id === props.defaultViewId,
                selectedVisibilityType: props.selectedView.visibility,
                newViewId: null,
                viewPermissions: this.props.viewPermissions
            };

            this._headerText = PackageResources.FeedSettings_ViewPanel_EditHeader;
            this._submitButtonText = PackageResources.FeedSettings_OkButton_Label;
            this._onClickAction = this._editView;
            this._disableSetDefaultView = this.state.defaultViewChecked;
        } else {
            // add
            newState = {
                viewName: "",
                defaultViewChecked: false,
                selectedVisibilityType: defaultVisibilityType,
                newViewId: Utils_String.newGuid(),
                viewPermissions: []
            };

            this._headerText = PackageResources.FeedSettings_ViewPanel_AddHeader;
            this._submitButtonText = PackageResources.FeedSettings_SaveButton_Label;
            this._onClickAction = this._addView;
            this._identity = null;
        }

        return newState;
    }

    private _isAddDisabled(): boolean {
        let hasChanges: boolean = false;
        if (this.props.isViewPanelInEditMode) {
            hasChanges =
                hasChanges ||
                this.props.selectedView.name !== this.state.viewName ||
                (this.props.selectedView.id !== this.props.defaultViewId && this.state.defaultViewChecked) ||
                this.props.selectedView.visibility !== this.state.selectedVisibilityType;
        }

        if (this.props.viewPermissions.length !== this.state.viewPermissions.length) {
            hasChanges = true;
        } else {
            for (let i: number = 0; i < this.props.viewPermissions.length; i++) {
                if (this.props.viewPermissions[i].identityId !== this.state.viewPermissions[i].identityId) {
                    hasChanges = true;
                    break;
                }
            }
        }

        return (
            (this.props.isViewPanelInEditMode && !hasChanges) ||
            this.state.viewName === "" ||
            this.props.validationErrorBag[FeedSettingsComponents.viewName] != null ||
            this.props.isSavingChanges
        );
    }

    private _isAddPermissionDisabled(): boolean {
        // if panel was just opened or user has entered invalid name for view
        return this._identity == null || this.props.validationErrorBag[FeedSettingsComponents.viewPermission] != null;
    }

    private _onViewNameChanged = (viewName: string): void => {
        this.setState({
            viewName
        });

        FeedSettingsActionCreator.onViewNameChanged.invoke({
            viewName,
            originalViewName: this.props.isViewPanelInEditMode ? this.props.selectedView.name : null
        });
    };

    private _onDefaultViewChange = (
        ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        checked: boolean
    ): void => {
        this.setState({
            defaultViewChecked: checked
        });
    };

    private _onDismiss = (): void => {
        FeedSettingsActionCreator.toggleViewPanelDisplay.invoke({ isOpen: false, isEditing: null });
    };

    private _addView = (): void => {
        FeedSettingsActionCreator.saveViewClicked.invoke({
            view: {
                /* Temp id for identifying the default view,
                    server will generate a new one when the view is saved. */
                id: this.state.newViewId,
                name: this.state.viewName,
                type: FeedViewType.Release,
                visibility: this.state.selectedVisibilityType
            } as FeedView,
            makeThisDefaultView: this.state.defaultViewChecked,
            viewPermissions: this.state.viewPermissions
        });
    };

    private _editView = (): void => {
        FeedSettingsActionCreator.saveViewClicked.invoke({
            view: {
                id: this.props.selectedView.id,
                name: this.state.viewName,
                type: this.props.selectedView.type,
                visibility: this.state.selectedVisibilityType
            } as FeedView,
            makeThisDefaultView: this.state.defaultViewChecked,
            viewPermissions: this.state.viewPermissions
        });
    };

    // tslint:disable-next-line:no-any
    private _onIdentitiesResolved = (identities: any[]): void => {
        if (identities == null || identities.length === 0) {
            return;
        }

        this._identity = identities[0];

        const permission: FeedPermission = this.getViewPermission();

        // This handler validate the selected permission
        FeedSettingsActionCreator.addViewPermissionChanged.invoke({ viewId: this._getViewId(), permission });
    };

    private _addViewPermission = (): void => {
        const permission: FeedPermission = this.getViewPermission();
        this._identity = null;

        const updatedPermissions: FeedPermission[] = this.state.viewPermissions.slice();
        updatedPermissions.push(permission);
        this.setState({ viewPermissions: updatedPermissions });
    };

    private _removeViewPermission = (permission: FeedPermission): void => {
        const updatedPermissions: FeedPermission[] = this.state.viewPermissions.slice();
        const index: number = updatedPermissions.findIndex(
            (viewPermission: FeedPermission) => viewPermission.identityId === permission.identityId
        );
        updatedPermissions.splice(index, 1);
        this.setState({ viewPermissions: updatedPermissions });
        announce(Utils_String.format(PackageResources.DeletedAnnouncement, permission.displayName));
    };

    private getViewPermission(): FeedPermission {
        if (this._identity == null) {
            return null;
        }

        const feedPermission: FeedPermission = {
            displayName: this._identity.displayName,
            identityId: this._identity.localId,
            role: FeedRole.Reader,
            isInheritedRole: false,
            identityDescriptor: null
        };

        return feedPermission;
    }

    private _getViewId(): string {
        return this.props.selectedView ? this.props.selectedView.id : this.state.newViewId;
    }

    private _getVisibility(s: string): FeedVisibility {
        return parseInt(s, 10);
    }
}
